import { CommentOverlayMode, OverlayTab } from "@/components/candidate-profile-overlay";

type OverlayAction =
    | { type: "close" }
    | { type: "selectCandidate"; candidateId: string }
    | { type: "setTab"; tab: OverlayTab }
    | { type: "setComment"; mode: CommentOverlayMode; commentId?: string | null }


export type CandidatesSearch = {
    candidateId?: string
    tab?: OverlayTab
    commentMode?: CommentOverlayMode
    commentId?: string
    newCandidate?:boolean
}

export function applyOverlayAction(prev: CandidatesSearch, action: OverlayAction): CandidatesSearch {
    switch (action.type) {
        case "close": {
            return {
                ...prev,
                candidateId: undefined,
                tab: undefined,
                commentMode: undefined,
                commentId: undefined,
            }
        }

        case "selectCandidate": {
            return {
                ...prev,
                candidateId: action.candidateId,
                tab: undefined,
                commentMode: undefined,
                commentId: undefined,
            }
        }

        case "setTab": {
            // Tabs and comments are mutually exclusive.
            // If tab is "none", we clear tab from URL.
            //
            // "none" is the Profile tab, and it has to clear the comment for the
            // same reason every other tab does. It used to clear only `tab`, so
            // an open comment survived the one click a reader would actually
            // make to leave it -- and since the comment view wins over the tab
            // view, the record could not be got back to at all.
            if (action.tab === "none") {
                return {
                    ...prev,
                    tab: undefined,
                    commentMode: undefined,
                    commentId: undefined,
                }
            }

            return {
                ...prev,
                tab: action.tab,
                commentMode: undefined,
                commentId: undefined,
            }
        }

        case "setComment": {
            // Comments and tabs are mutually exclusive.
            if (action.mode === "none") {
                return {
                    ...prev,
                    commentMode: undefined,
                    commentId: undefined,
                }
            }

            if (action.mode === "list") {
                return {
                    ...prev,
                    tab: undefined,
                    commentMode: "list",
                    commentId: undefined,
                }
            }

            if (action.mode === "view") {
                // Enforce invariant: view requires an id. If missing, do nothing (or clear).
                if (!action.commentId) return prev
                return {
                    ...prev,
                    tab: undefined,
                    commentMode: "view",
                    commentId: action.commentId,
                }
            }

            // create
            return {
                ...prev,
                tab: undefined,
                commentMode: "create",
                commentId: undefined,
            }
        }

        default: {
            // Exhaustiveness guard (helps when you add new actions)
            return prev
        }
    }
}

const isOverlayTab = (v: unknown): v is OverlayTab =>
  v === "none" ||
  v === "resume" ||
  v === "outreach" ||
  v === "timeline" ||
  v === "events" ||
  v === "placements" ||
  v === "tasks" ||
  v === "edit"

const isCommentMode = (v: unknown): v is CommentOverlayMode =>
  v === "none" || v === "list" || v === "create" || v === "view"

export function ValidateSearch(search: Record<string, unknown>): CandidatesSearch{
    const candidateId = typeof search.candidateId === "string" ? search.candidateId : undefined
    const rawNewCandidate= search.newCandidate 
    const newCandidate =
    rawNewCandidate === true ||
    rawNewCandidate === "true" ||
    rawNewCandidate === "1"
    
    // only allowing newCandidate to exist if no candidate selected
    if (!candidateId) return { newCandidate: newCandidate? true:undefined } 

    const tab = isOverlayTab(search.tab) ? search.tab : undefined

    const commentMode = isCommentMode(search.commentMode) ? search.commentMode : undefined
    const commentId = typeof search.commentId === "string" ? search.commentId : undefined

    // normalize invalid combos
    if (commentMode === "view" && !commentId) {
      return { candidateId, tab } // drop broken view state
    }

    // enforce mutual exclusivity: comments win
    if (commentMode && commentMode !== "none") {
      return { candidateId, commentMode, commentId }
    }

    // otherwise tab state
    return { candidateId, tab }
  }
